import React from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Ratio from 'react-bootstrap/Ratio'
import ProgressBar from 'react-bootstrap/ProgressBar'
import { getYouTubeId } from '../../../utils/youtube'

const MAX_VIDEO_MB = 200

const TYPE_OPTIONS = [
    { value: 'pdf', label: 'Documento PDF' },
    { value: 'youtube', label: 'Video de YouTube' },
    { value: 'mp4', label: 'Video MP4 (archivo)' },
]

function formatMb(bytes) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const CulturaInstitucionalForm = ({ material, videoUrl }) => {
    const { flash } = usePage().props
    const isEdit = Boolean(material)

    const form = useForm({
        title: material?.title || '',
        type: material?.type || 'pdf',
        sort_order: material?.sort_order ?? 0,
        document: null,
        youtube_url: material?.youtube_url || '',
        video: null,
    })

    const type = form.data.type
    const youtubeId = getYouTubeId(form.data.youtube_url)
    const hasInvalidUrl = type === 'youtube' && form.data.youtube_url.trim() !== '' && !youtubeId
    const videoTooLarge = form.data.video && form.data.video.size > MAX_VIDEO_MB * 1024 * 1024

    const fieldError = (key) => {
        const e = form.errors[key]
        return Array.isArray(e) ? e[0] : e
    }

    const path = isEdit
        ? `/admin/sia/manage/cultura-institucional/${material.id}/edit`
        : '/admin/sia/manage/cultura-institucional/create'

    const submit = (e) => {
        e.preventDefault()
        if (videoTooLarge) return

        form.transform((data) => ({
            ...(isEdit ? { _method: 'put' } : {}),
            title: data.title,
            type: data.type,
            sort_order: Number(data.sort_order),
            document: data.type === 'pdf' ? data.document : null,
            youtube_url: data.type === 'youtube' ? data.youtube_url.trim() : '',
            video: data.type === 'mp4' ? data.video : null,
        }))

        const url = isEdit
            ? route('sia.manage.cultura-institucional.update', material.id)
            : route('sia.manage.cultura-institucional.store')

        form.post(url, { preserveScroll: true, forceFormData: true })
    }

    const replaceWarning = () => {
        if (!isEdit) return null
        if (type !== 'pdf' && material.file_path) {
            return `Al guardar se eliminará el PDF actual (${material.original_filename || material.file_path}).`
        }
        if (type !== 'youtube' && material.type === 'youtube' && material.youtube_url) {
            return 'Al guardar se quitará el video de YouTube actual.'
        }
        if (type !== 'mp4' && material.video_path) {
            return `Al guardar se eliminará el video MP4 actual (${material.video_original_filename || 'archivo'}).`
        }
        return null
    }

    const warning = replaceWarning()
    const uploading = form.processing && form.progress && type === 'mp4' && form.data.video

    return (
        <>
            <div className="mb-5 mt-5">
                <Breadcrumbs currentPath={path} />
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-6">
                <h1 className="fs-2 fw-bold text-gray-900 mb-0">{isEdit ? 'Editar material' : 'Nuevo material'}</h1>
                <Link href={route('sia.manage.cultura-institucional.index')} className="btn btn-sm btn-light">
                    ← Volver al listado
                </Link>
            </div>

            {flash.success && <div className="alert alert-success mb-5">{flash.success}</div>}
            {flash.error && <div className="alert alert-danger mb-5">{flash.error}</div>}

            <Card className="card-flush shadow-sm">
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit}>
                        <Row className="g-4 mb-4">
                            <Col md={8}>
                                <Form.Label className="fw-bold">Título</Form.Label>
                                <Form.Control
                                    value={form.data.title}
                                    onChange={(e) => form.setData('title', e.target.value)}
                                    className={fieldError('title') ? 'is-invalid' : ''}
                                />
                                {fieldError('title') && <div className="invalid-feedback d-block">{fieldError('title')}</div>}
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold">Orden</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    value={form.data.sort_order}
                                    onChange={(e) => form.setData('sort_order', parseInt(e.target.value, 10) || 0)}
                                    className={fieldError('sort_order') ? 'is-invalid' : ''}
                                />
                                {fieldError('sort_order') && <div className="invalid-feedback d-block">{fieldError('sort_order')}</div>}
                            </Col>
                        </Row>

                        <Row className="g-4 mb-4">
                            <Col md={8}>
                                <Form.Label className="fw-bold d-block">Tipo de material</Form.Label>
                                {TYPE_OPTIONS.map((opt) => (
                                    <Form.Check
                                        key={opt.value}
                                        inline
                                        type="radio"
                                        id={`material-type-${opt.value}`}
                                        name="type"
                                        label={opt.label}
                                        checked={type === opt.value}
                                        onChange={() => form.setData('type', opt.value)}
                                    />
                                ))}
                                {fieldError('type') && <div className="invalid-feedback d-block">{fieldError('type')}</div>}
                                {warning && <div className="alert alert-warning py-2 px-3 mt-3 mb-0 fs-7">{warning}</div>}
                            </Col>
                        </Row>

                        <Row className="g-4 mb-6">
                            {type === 'pdf' && (
                                <Col md={8}>
                                    <Form.Label className="fw-bold">Archivo PDF</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        onChange={(e) => form.setData('document', e.target.files[0] || null)}
                                        className={fieldError('document') ? 'is-invalid' : ''}
                                    />
                                    {isEdit && material.file_path && (
                                        <Form.Text muted>Archivo actual: {material.original_filename || material.file_path}</Form.Text>
                                    )}
                                    {fieldError('document') && <div className="invalid-feedback d-block">{fieldError('document')}</div>}
                                </Col>
                            )}

                            {type === 'youtube' && (
                                <>
                                    <Col md={8}>
                                        <Form.Label className="fw-bold">URL del video de YouTube</Form.Label>
                                        <Form.Control
                                            value={form.data.youtube_url}
                                            onChange={(e) => form.setData('youtube_url', e.target.value)}
                                            placeholder="https://www.youtube.com/watch?v=…"
                                            className={fieldError('youtube_url') || hasInvalidUrl ? 'is-invalid' : ''}
                                        />
                                        <Form.Text muted>Acepta enlaces de youtube.com, youtu.be y Shorts.</Form.Text>
                                        {fieldError('youtube_url') ? (
                                            <div className="invalid-feedback d-block">{fieldError('youtube_url')}</div>
                                        ) : (
                                            hasInvalidUrl && <div className="invalid-feedback d-block">URL de YouTube no válida.</div>
                                        )}
                                    </Col>
                                    {youtubeId && (
                                        <Col md={8}>
                                            <Form.Label className="fw-bold">Vista previa</Form.Label>
                                            <div className="rounded overflow-hidden border border-gray-300" style={{ maxWidth: '480px' }}>
                                                <Ratio aspectRatio="16x9">
                                                    <iframe
                                                        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0`}
                                                        title="Vista previa del video"
                                                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                </Ratio>
                                            </div>
                                        </Col>
                                    )}
                                </>
                            )}

                            {type === 'mp4' && (
                                <>
                                    <Col md={8}>
                                        <Form.Label className="fw-bold">Archivo de video MP4</Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept="video/mp4,.mp4,.m4v,.mov"
                                            disabled={form.processing}
                                            onChange={(e) => form.setData('video', e.target.files[0] || null)}
                                            className={fieldError('video') || videoTooLarge ? 'is-invalid' : ''}
                                        />
                                        <Form.Text muted>
                                            Máximo {MAX_VIDEO_MB} MB. Después de subirlo, el video se optimiza automáticamente (puede tardar
                                            unos minutos) y te avisaremos por correo cuando esté disponible.
                                        </Form.Text>
                                        {form.data.video && !videoTooLarge && (
                                            <div className="text-gray-700 fs-7 mt-1">
                                                Seleccionado: {form.data.video.name} ({formatMb(form.data.video.size)})
                                            </div>
                                        )}
                                        {videoTooLarge && (
                                            <div className="invalid-feedback d-block">El video no debe pesar más de {MAX_VIDEO_MB} MB.</div>
                                        )}
                                        {fieldError('video') && <div className="invalid-feedback d-block">{fieldError('video')}</div>}
                                        {isEdit && material.type === 'mp4' && material.video_status === 'failed' && (
                                            <div className="alert alert-danger py-2 px-3 mt-3 mb-0 fs-7">
                                                No se pudo procesar el video anterior. Sube el archivo de nuevo o elimina el material.
                                            </div>
                                        )}
                                        {isEdit && material.type === 'mp4' && material.video_status === 'ready' && !form.data.video && (
                                            <Form.Text muted className="d-block">
                                                Video actual: {material.video_original_filename || 'archivo'}
                                                {material.video_size ? ` (${formatMb(material.video_size)} optimizado)` : ''}. Selecciona otro
                                                archivo solo si quieres reemplazarlo.
                                            </Form.Text>
                                        )}
                                    </Col>
                                    {videoUrl && !form.data.video && (
                                        <Col md={8}>
                                            <Form.Label className="fw-bold">Vista previa</Form.Label>
                                            <div className="rounded overflow-hidden border border-gray-300 bg-black" style={{ maxWidth: '480px' }}>
                                                <Ratio aspectRatio="16x9">
                                                    <video src={videoUrl} controls preload="metadata" controlsList="nodownload" />
                                                </Ratio>
                                            </div>
                                        </Col>
                                    )}
                                </>
                            )}
                        </Row>

                        {uploading && (
                            <div className="mb-5" style={{ maxWidth: '480px' }}>
                                <div className="fs-7 text-gray-700 mb-1">Subiendo video… {form.progress.percentage}%</div>
                                <ProgressBar now={form.progress.percentage} animated striped />
                            </div>
                        )}

                        <Button type="submit" variant="primary" disabled={form.processing || videoTooLarge || hasInvalidUrl}>
                            {form.processing ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear material'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </>
    )
}

CulturaInstitucionalForm.layout = (page) => (
    <Layout title={page.props.material?.id ? 'Editar material' : 'Nuevo material'}>{page}</Layout>
)

export default CulturaInstitucionalForm
